import { Link, useParams } from 'react-router-dom';
import { Icon } from '@/features/common/Icon';
import { usePublicList } from '@/features/publicsite/usePublicList';
import { usePublicPageTitle } from '@/features/publicsite/usePublicPageTitle';
import type { Association } from '@/features/publicsite/types/community/Association';
import { publicCommunity } from '@/i18n/dictionaries/publicCommunity';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * An association's own page — until now its card on /comunitate had nowhere to lead, the one
 * thing CatalogCard's own doc comment says a card must never pretend to have.
 *
 * Filters the public list rather than calling a dedicated detail endpoint, the same choice
 * ResearchOrganizationProfilePage makes: registerAssociation only ever collects a name and an
 * optional fiscal code (CommunityService.registerAssociation), so there is nothing a second
 * endpoint would answer that this list does not already carry.
 */
export function AssociationPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation(publicCommunity);
  const associations = usePublicList<Association>('/api/v1/public/associations');
  const association = associations?.find((a) => a.id === id) ?? null;

  usePublicPageTitle(
    associations === null ? null : (association?.name ?? t('associationNotFound')),
  );

  if (associations === null) {
    return (
      <section className="container section">
        <p className="empty-state">{t('loading')}</p>
      </section>
    );
  }
  if (!association) {
    return (
      <section className="container section">
        <h1>{t('associationNotFound')}</h1>
        <p>
          <Link to="/comunitate">{t('backToCommunity')}</Link>
        </p>
      </section>
    );
  }

  return (
    <section className="container section">
      <Link className="detail-back" to="/comunitate">
        <Icon name="chevron-left" className="detail-back-icon" />
        {t('backToCommunity')}
      </Link>
      <p className="eyebrow">{t('associationBadge')}</p>
      <h1>{association.name}</h1>
      {association.fiscalCode && (
        <p className="muted">{t('associationFiscalCode', { code: association.fiscalCode })}</p>
      )}
    </section>
  );
}
